/**
 * Challenge Component
 * Where the user enters the their email or phone number to receive a challenge code
 */
'use client'

import { parsePhoneNumberWithError } from 'libphonenumber-js'
import Link from 'next/link'
import React, { useCallback, useEffect } from 'react'

import { Input, SolidButton } from '.'
import { KeyIcon } from '../icons'
import { isEmailAddress } from '../utils/validation'

interface IdentifierFormProps extends React.InputHTMLAttributes<HTMLInputElement> {
  authMode: 'email_otp' | 'sms_otp'
  handleMFAChallenge: (e: React.FormEvent<HTMLFormElement>) => void
  identifier: string
  setIdentifier: (identifier: string) => void
  loading: boolean
  error: string
  emailOptIn: boolean
  setEmailOptIn: (emailOptIn: boolean) => void
  setError: (error: string) => void
}

export default function IdentifierForm ({
  authMode,
  handleMFAChallenge,
  identifier,
  setIdentifier,
  loading,
  error,
  emailOptIn,
  setEmailOptIn,
  setError,
  ...props
}: IdentifierFormProps): React.JSX.Element {
  const isValidIdentifier = (input: string): boolean => {
    if (!input) return false
    if (isEmailAddress(input)) return true

    try {
      const parsedPhoneNumber = parsePhoneNumberWithError(input, { defaultCountry: 'US' })
      return parsedPhoneNumber.isValid()
    } catch (error) {
      return false
    }
  }

  // Validation with error handling
  const validateAndSetError = useCallback((identifier: string): void => {
    if (identifier.length === 0) return

    // Check if it looks like an email (contains @)
    if (identifier.includes('@')) {
      // Treat as email attempt
      if (isEmailAddress(identifier)) setError('')
      else setError('Please enter a valid email address')
    } else if (identifier.startsWith('+') || /^\d+$/.test(identifier)) {
      // Treat as phone number attempt (starts with + or is all digits)
      if (isValidIdentifier(identifier)) setError('')
      else setError('Please enter a valid phone number (format: +1234567890)')
    } else {
      // Ambiguous input - provide general guidance
      setError('Please enter a valid email address or phone number')
    }
  }, [setError])

  // Debounced validation effect
  useEffect(() => {
    if (identifier.length === 0) return

    const timeoutId = setTimeout(() => {
      if (identifier.length >= 8) validateAndSetError(identifier)
    }, 800) // 800ms debounce

    return () => clearTimeout(timeoutId)
  }, [identifier, validateAndSetError])

  return (
    <div>
      <KeyIcon height={96} className="mx-auto -rotate-45" />

      <h2 className="mt-6 text-center text-2xl font-extrabold text-neutrals-800">
        Log in or sign up
      </h2>

      <form className="mt-4 space-y-4" onSubmit={handleMFAChallenge}>
        <div>
          <label htmlFor="identifier" className="block text-sm font-medium text-neutrals-800">
            Email or Phone
          </label>
          <Input
            id="identifier"
            name="identifier"
            type="text"
            data-testid="identifier-input"
            autoComplete={authMode === 'email_otp' ? 'email' : 'tel'}
            required
            value={identifier}
            error={!!error}
            onBlur={() => {
              validateAndSetError(identifier)
            }}
            onChange={e => {
              let inputValue = e.target.value.replace(/\s/g, '')
              setIdentifier(inputValue)

              // Clear error when input is empty or becomes valid
              if (inputValue === '' || isValidIdentifier(inputValue)) {
                setError('')
              }

              // Only process as phone number if it's numeric (digits only or starts with +)
              if (!inputValue.startsWith('+') && !/^\d+$/.test(inputValue)) return

              try {
                const parsedPhoneNumber = parsePhoneNumberWithError(inputValue, { defaultCountry: 'US' })
                if (parsedPhoneNumber?.isValid()) {
                  setIdentifier(parsedPhoneNumber.number)
                } else {
                  // Prepend + if it's more than 10 characters and doesn't already start with +
                  if (inputValue.length > 10 && !inputValue.startsWith('+')) {
                    inputValue = '+' + inputValue
                    setIdentifier(inputValue)
                  }
                }
              } catch (error) {
                // Silently ignore parsing errors - let validation handle invalid formats
              }
            }}
            placeholder="Enter your email or phone number"
          />

          {error && (
            <div className="mt-2 font-medium text-xs text-error">
              {error}
            </div>
          )}
        </div>

        <SolidButton type="submit" disabled={loading || !!error || !isValidIdentifier(identifier)} data-testid="send-code-button">
          Send code
        </SolidButton>

        <div className="mt-6 space-y-6">
          <div className="text-center">
            <p className="text-sm text-neutrals-600 font-medium leading-relaxed max-w-[250px] mx-auto">
              By continuing, you agree to our <Link href="https://hello.mapquest.com/legal" target="_blank" className="text-accent-400 underline" rel="noreferrer" data-testid="terms-of-service-link">Terms of Service</Link> and <Link href="https://hello.mapquest.com/privacy" target="_blank" className="text-accent-400 underline" rel="noreferrer" data-testid="privacy-policy-link">Privacy Policy</Link>
            </p>
          </div>

          <div className="flex items-start space-x-2">
            <input
              id="email-opt-in"
              name="email-opt-in"
              type="checkbox"
              data-testid="email-opt-in-checkbox"
              checked={emailOptIn}
              onChange={e => setEmailOptIn(e.target.checked)}
              className="h-7 w-7 accent-primary-800 focus:ring-primary-500 border-neutrals-600 rounded"
            />
            <label htmlFor="email-opt-in" className="text-sm text-neutrals-600 font-medium" data-testid="email-opt-in-label">
              Yes! Send me offers, news, and updates about my account.
            </label>
          </div>
        </div>
      </form>
    </div>
  )
}
